import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { api } from "@/lib/api-client";
import { useForm } from "@mantine/form";
import {
  addToast,
  Button,
  Chip,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Tab,
  Tabs,
  useDisclosure,
} from "@heroui/react";
import {
  createPdfPrintData,
  getPaymentTypeLabel,
  PaymentType,
  preparePrintData,
  shouldPrintInvoiceAsPdf,
} from "@/utils/pos-printer/index";
import {
  PrintReceiptType,
  usePosPrinterStore,
} from "@/stores/pos-printer-store";
import { getApiErrorToast } from "@/utils/api-error";
import { saveBase64File } from "@/utils/saveFile";
import { toastPromise } from "@/utils/toast-utils";
import { useEffect } from "react";
import { SplitPaymentForm, SplitPaymentModal } from "./POS/SplitPaymentModal";
import { SendInvoiceEmailButton } from "./SendInvoiceEmailButton";
import {
  canChangeInvoicePaymentMethod,
  getInvoiceEmailRecipient,
  getInvoiceShareDocumentIds,
  getSharesToMarkPaidOnPayment,
  getSharesToMarkUnpaidOnCancel,
} from "./invoice-actions";
import { hasCapability } from "../providerCapabilities";
import { SubscriptionShareModal } from "./SubscriptionShareModal";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import {
  Download,
  FileCheck2,
  FileX2,
  Printer,
  PrinterCheck,
  Replace,
} from "lucide-react";
import type { PutInvoicePaymentMethod } from "@/server-types";

export const InvoiceDetailsModal = ({
  isAuthenticated,
  invoiceId,
  onClose,
  disableNavigationOnCancel,
  redirectUrl,
}: {
  isAuthenticated: boolean;
  invoiceId?: string;
  onClose: () => void;
  disableNavigationOnCancel?: boolean;
  /** Where the POS returns after issuing the invoice. Defaults to this page. */
  redirectUrl?: string;
}) => {
  const {
    isOpen: isChangePaymentsModalOpen,
    onOpen: openChangePaymentsModal,
    onClose: closeChangePaymentsModal,
  } = useDisclosure();

  const {
    isOpen: isSubscriptionSharePaymentModalOpen,
    onOpen: openSubscriptionSharePaymentModal,
    onClose: closeSubscriptionSharePaymentModal,
  } = useDisclosure();

  const {
    isOpen: isSubscriptionShareCancelModalOpen,
    onOpen: openSubscriptionShareCancelModal,
    onClose: closeSubscriptionShareCancelModal,
  } = useDisclosure();

  const {
    data: invoice,
    isPending,
    isFetching,
    isError,
    refetch,
    processedErrorMessage: getInvoiceErrorMessage,
  } = api.taxRegister.useGetOrganizationInvoice({
    invoiceId,
    supportsDiacritics: false,
  });

  // The server answers for the document's own organization, which can be an archived one.
  const isReadOnly = invoice?.readOnly === true;

  // A dobropis is settled by the storno itself: payments and a further
  // cancellation are driven from the invoice it reverses, not from here
  const isCreditNote = invoice?.type === "credit-note";

  const emailRecipient = getInvoiceEmailRecipient(invoice?._documentClient);

  const {
    data: subscriptionShares,
    isLoading: isSubscriptionSharesLoading,
    processedErrorMessage: getSubscriptionSharesErrorMessage,
  } = api.customer.useGetSubscriptionInvoiceShares({
    spaceInvoicesDocumentIds: getInvoiceShareDocumentIds(invoice),
  });

  const sharesToMarkUnpaidOnCancel =
    getSharesToMarkUnpaidOnCancel(subscriptionShares);
  const sharesToMarkPaidOnPayment =
    getSharesToMarkPaidOnPayment(subscriptionShares);

  const recordedPayments = invoice?.additionalReceiptData.payments ?? [];

  // Space Invoices does not always tag a document as paid in full, so the
  // outstanding part is derived from the recorded payments (same reasoning as
  // getInvoiceStatus on the server).
  const outstandingCents =
    Math.round((invoice?.totalWithTax ?? 0) * 100) -
    recordedPayments.reduce(
      (acc, payment) => acc + Math.round(payment.amount * 100),
      0,
    );

  const existingPaymentsForChange: PutInvoicePaymentMethod["body"]["payments"] =
    recordedPayments.map((payment) => {
      if (payment.type === "coupon") {
        return {
          type: "coupon" as const,
          amountCents: Math.round(payment.amount * 100),
          giftCardCode: payment.code ?? "",
        };
      }

      return {
        type: payment.type as "cash" | "card" | "online" | "bank",
        amountCents: Math.round(payment.amount * 100),
      };
    });

  // Settling the rest by TRR must keep the payments already recorded — the
  // endpoint deletes every payment type missing from the body.
  const settleOutstandingPayments: PutInvoicePaymentMethod["body"]["payments"] =
    existingPaymentsForChange.some((payment) => payment.type === "bank")
      ? existingPaymentsForChange.map((payment) =>
          payment.type === "bank"
            ? {
                ...payment,
                amountCents: payment.amountCents + outstandingCents,
              }
            : payment,
        )
      : [
          ...existingPaymentsForChange,
          { type: "bank" as const, amountCents: outstandingCents },
        ];

  const { data: receiptSettings } =
    api.taxRegister.useGetTaxRegisterFiscalizationSettings(isAuthenticated);

  const { data: organizationData } = api.taxRegister.useGetOrganization({
    disabled: !isAuthenticated,
  });

  const capabilities = organizationData?.capabilities;
  const canEditPayments = hasCapability(capabilities, "paymentEdit");

  const isTrrReady =
    organizationData != null &&
    organizationData.IBAN != null &&
    organizationData.IBAN.length > 0 &&
    organizationData.SWIFT != null &&
    organizationData.SWIFT.length > 0 &&
    organizationData.bank != null &&
    organizationData.bank.length > 0;

  const { isPrinting, printReceipt, selectedPrinterType } = usePosPrinterStore(
    (state) => state,
  );

  const {
    mutateAsync: postSendInvoiceEmail,
    isPending: isPostSendInvoiceEmailPending,
    processedErrorMessage: postSendInvoiceEmailErrorMessage,
  } = api.taxRegister.usePostSendInvoiceEmail();

  const {
    mutateAsync: postCancelInvoice,
    isPending: isPostCancelInvoicePending,
    processedErrorMessage: postCancelInvoiceErrorMessage,
  } = api.taxRegister.usePostCancelInvoice();

  const {
    mutateAsync: putOrganizationInvoicePrintCount,
    isPending: isPutOrganizationInvoicePrintCountPending,
    processedErrorMessage: putOrganizationInvoicePrintCountErrorMessage,
  } = api.taxRegister.usePutOrganizationInvoicePrintCount();

  const { mutateAsync: getInvoicePdfForPrint } =
    api.taxRegister.useGetDownloadInvoice();

  const {
    mutateAsync: putInvoicePaymentMethod,
    isPending: isPutInvoicePaymentMethodPending,
    processedErrorMessage: putInvoicePaymentMethodErrorMessage,
  } = api.taxRegister.usePutInvoicePaymentMethod();

  const {
    mutateAsync: getDownloadInvoice,
    isPending: isGetDownloadInvoicePending,
  } = api.taxRegister.useGetDownloadInvoice();

  const {
    data: locationsData,
    processedErrorMessage: getLocationsErrorMessage,
  } = api.location.useGetLocations({
    page: 1,
    perPage: 100,
    sortBy: "labelDesc",
  });

  const handlePrintReceipt = async ({ type }: { type: PrintReceiptType }) => {
    if (!invoice) {
      addToast({
        title: t`Invoice not found`,
        color: "danger",
      });
      return;
    }

    const printerConfig =
      selectedPrinterType === "qztray"
        ? locationsData?.locations.find(
            (location) => location.locationId == invoice.locationId,
          )?.PosPrinter || null
        : null;

    const printReceiptPromise = async () => {
      if (type === "receipt" && shouldPrintInvoiceAsPdf(printerConfig)) {
        // Fetching the PDF already counts as a print in Space Invoices.
        const pdfBase64 = await getInvoicePdfForPrint({
          invoiceId: invoice.id,
        });

        await printReceipt({
          printData: createPdfPrintData(pdfBase64),
          printerConfig,
          shouldThrow: true,
        });
        return;
      }

      const printData =
        type === "receipt"
          ? await preparePrintData(
              {
                type: "invoice",
                invoiceData: invoice,
                isCopy: true,
              },
              printerConfig,
              {
                receiptHeader: receiptSettings?.receiptHeader ?? undefined,
                receiptFooter: receiptSettings?.receiptFooter ?? undefined,
              },
            )
          : await preparePrintData(
              {
                type: "transaction-account",
                data: {
                  ...invoice,
                  amount: invoice.totalDue,
                  amountFormatted: invoice.totalDueFormatted,
                  currency: invoice.currencyId,
                  invoiceNumber: invoice.number,
                  name: invoice.additionalReceiptData._documentIssuer.name,
                  address:
                    invoice.additionalReceiptData._documentIssuer.address,
                  zip: invoice.additionalReceiptData._documentIssuer.zip,
                  city: invoice.additionalReceiptData._documentIssuer.city,
                  country:
                    invoice.additionalReceiptData._documentIssuer.country,
                  IBAN: invoice.additionalReceiptData._documentIssuer.IBAN,
                  SWIFT: invoice.additionalReceiptData._documentIssuer.SWIFT,
                  reference: `SI00 ${invoice.numberOnly}`,
                  dateDue: invoice.dateDue,
                },
              },
              printerConfig,
            );

      await printReceipt({
        printData,
        printerConfig,
        shouldThrow: true,
      });

      await putOrganizationInvoicePrintCount(invoiceId);
    };

    toastPromise(printReceiptPromise(), {
      loading: t`Dokument se tiska...`,
      success: async () => {
        await refetch();
        return t`Dokument je bil tiskan`;
      },
      error: (err) =>
        getApiErrorToast(
          err,
          t`Prišlo je do napake pri tiskanju računa. Preverite povezavo z tiskalnikom`,
        ),
    });
  };

  const settleOutstanding = async (markSubscriptionSharesPaid: boolean) => {
    if (!invoice) return;

    await putInvoicePaymentMethod({
      invoiceId: invoice.id,
      body: { payments: settleOutstandingPayments, markSubscriptionSharesPaid },
    });

    refetch();
  };

  const form = useForm<{
    cancellationReason: string;
  }>({
    initialValues: {
      cancellationReason: "",
    },
    validate: {
      cancellationReason: (value) => {
        if (invoice?.type === "estimate") {
          return null;
        }

        return value.length > 0 ? null : t`Razlog stornacije je obvezen`;
      },
    },
  });

  const cancelInvoice = async (markSubscriptionSharesUnpaid: boolean) => {
    if (!invoiceId) return;

    const response = await postCancelInvoice({
      invoiceId,
      body: {
        cancellationReason: form.values.cancellationReason,
        markSubscriptionSharesUnpaid,
      },
    });

    addToast({
      title:
        response.type === "estimate"
          ? t`Predračun je bil izbrisan`
          : t`Dokument je bil storniran`,
      color: "success",
    });

    // A storno keeps the document open on its own id, and the invalidated
    // query re-renders it as canceled
    if (disableNavigationOnCancel || response.type === "estimate") {
      onClose();
    }
  };

  useEffect(() => {
    if (!invoiceId) {
      form.reset();
    }
  }, [invoiceId]);

  return (
    <>
      <Modal
        size="xl"
        isOpen={invoiceId != null && isAuthenticated}
        onClose={onClose}
        scrollBehavior="outside"
        data-identifier="invoice-details-modal"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {invoice?.typeLocalized} {invoice?.number}
              </ModalHeader>
              <ModalBody>
                <LimeAlert message={getLocationsErrorMessage} />
                <LimeAlert
                  message={putOrganizationInvoicePrintCountErrorMessage}
                />

                {/** Disable animation because it is inside a modal, otherwise modal does not close correctly */}
                <Tabs fullWidth disableAnimation>
                  <Tab
                    title={invoice?.typeLocalized ?? t`Račun`}
                    className="flex flex-col gap-3"
                  >
                    <InvoiceDetailRow
                      label={t`Številka dokumenta`}
                      value={invoice?.number || "XXXX-XXXXX"}
                      isSkeleton={isPending}
                    />
                    {invoice?.reference ? (
                      <InvoiceDetailRow
                        label={t`Referenca`}
                        value={invoice?.reference || "XXXX-XXXXX"}
                        isSkeleton={isPending}
                      />
                    ) : undefined}
                    {invoice?.canceledInvoiceNumber ? (
                      <InvoiceDetailRow
                        label={t`Številka storniranega dokumenta`}
                        value={
                          invoice.reversedInvoiceId ? (
                            <Link
                              showAnchorIcon
                              className="text-sm"
                              href={`/dashboard/tax-register/invoice/${invoice.reversedInvoiceId}`}
                            >
                              {invoice.canceledInvoiceNumber}
                            </Link>
                          ) : (
                            invoice.canceledInvoiceNumber
                          )
                        }
                        isSkeleton={isPending}
                      />
                    ) : undefined}
                    <InvoiceDetailRow
                      label={t`Datum in čas`}
                      value={invoice?.date || "XX. XX. XXXX XX:XX"}
                      isSkeleton={isPending}
                    />
                    <InvoiceDetailRow
                      label={t`Datum zapadlosti`}
                      value={invoice?.dateDueFormatted || "XX. XX. XXXX XX:XX"}
                      isSkeleton={isPending}
                    />
                    <InvoiceDetailRow
                      label={
                        invoice?.canceled ? t`Originalni znesek` : t`Znesek`
                      }
                      value={invoice?.totalFormatted || "XX.XXX,XX X"}
                      isSkeleton={isPending}
                    />
                    {invoice?.creditNoteTotalFormatted ? (
                      <InvoiceDetailRow
                        label={t`Dobropis`}
                        value={
                          invoice?.creditNoteTotalFormatted || "XX.XXX,XX X"
                        }
                        isSkeleton={isPending}
                      />
                    ) : undefined}
                    {invoice?.type === "estimate" ? null : (
                      <InvoiceDetailRow
                        label={t`Način plačila`}
                        value={
                          invoice ? (
                            <>
                              {invoice.additionalReceiptData.payments.map(
                                (payment) => (
                                  <div
                                    key={payment.type}
                                    className="flex w-full justify-between gap-2 text-sm text-gray-500"
                                    data-identifier={`payment-row-${payment.type}`}
                                  >
                                    <p className="w-fit text-left">
                                      {getPaymentTypeLabel(
                                        payment.type as PaymentType,
                                      )}
                                      {payment.type === "coupon" ? (
                                        <Link
                                          href={`/dashboard/gift-card/${payment.giftCardId}`}
                                          showAnchorIcon
                                          className="ml-1 w-fit text-left"
                                          size="sm"
                                        >
                                          {payment.code}
                                        </Link>
                                      ) : undefined}
                                    </p>
                                    <p className="w-full flex-1 text-right">
                                      {payment.amountFormatted}
                                    </p>
                                  </div>
                                ),
                              )}
                              {invoice.additionalReceiptData.payments.length ===
                                0 && (
                                <p className="text-sm text-gray-500">
                                  <Trans>Ni plačila</Trans>
                                </p>
                              )}
                            </>
                          ) : (
                            "XXXXXXXXX"
                          )
                        }
                        isSkeleton={isPending}
                      />
                    )}
                    {invoice?.type === "estimate" ? null : (
                      <InvoiceDetailRow
                        label={t`Stanje dokumenta`}
                        value={(() => {
                          const color = (() => {
                            switch (invoice?.status.value) {
                              case "paid":
                              case "payment_not_required":
                                return "success";
                              case "paid_partially":
                                return "warning";
                              case "canceled":
                                return "danger";
                              case "not_paid":
                                return "danger";
                              default:
                                return "default";
                            }
                          })();

                          return (
                            <Chip
                              size="sm"
                              color={color}
                              variant="flat"
                              data-identifier={invoice?.status.value}
                            >
                              {invoice?.status.label}
                            </Chip>
                          );
                        })()}
                        isSkeleton={isPending}
                      />
                    )}

                    <InvoiceDetailRow
                      label={t`Izdal`}
                      value={
                        invoice?.additionalReceiptData.employeeData.name ||
                        "XXXX XXXXX"
                      }
                      isSkeleton={isPending}
                    />

                    {invoice?._documentClient.customerData && (
                      <InvoiceDetailRow
                        label={t`Končna stranka`}
                        value={
                          invoice._documentClient.customerData.name ||
                          "XXXX XXXXX"
                        }
                        isSkeleton={isPending}
                      />
                    )}

                    {invoice?._documentClient.companyData && (
                      <InvoiceDetailRow
                        label={t`Končno podjetje`}
                        value={
                          invoice._documentClient.companyData.companyName ||
                          "XXXX XXXXX"
                        }
                        isSkeleton={isPending}
                      />
                    )}

                    {invoice?.referencedDocuments?.length ? (
                      <>
                        <Divider />
                        <p className="font-semibold">
                          <Trans>Povezani dokumenti</Trans>
                        </p>
                        {invoice?.referencedDocuments?.map((document) => (
                          <InvoiceDetailRow
                            label={(() => {
                              if (document.type === "estimate")
                                return t`Predračun`;
                              if (document.type === "credit-note")
                                return t`Dobropis`;
                              return t`Račun`;
                            })()}
                            value={
                              <Link
                                showAnchorIcon
                                className="text-sm"
                                href={`/dashboard/tax-register/invoice/${document.id}`}
                              >
                                {document.number}
                              </Link>
                            }
                            isSkeleton={isPending}
                          />
                        ))}
                      </>
                    ) : null}

                    {invoice?.canceled && (
                      <div>
                        <p className="font-semibold text-red-500 uppercase">
                          {invoice.type === "invoice" ? (
                            <Trans>Storniran</Trans>
                          ) : (
                            <Trans>Uporabljen</Trans>
                          )}{" "}
                          {invoice.creditNote ? (
                            <Link
                              showAnchorIcon
                              className="text-sm text-red-500"
                              href={`/dashboard/tax-register/invoice/${invoice.creditNote.id}`}
                            >
                              {invoice.creditNote.number}
                            </Link>
                          ) : null}
                        </p>
                        {invoice.cancellationReason != null && (
                          <p className="text-sm text-gray-500">
                            {invoice.cancellationReason}
                          </p>
                        )}
                      </div>
                    )}

                    <Divider />

                    <p className="font-semibold">
                      <Trans>Artikli</Trans>
                    </p>
                    {invoice?.additionalReceiptData.invoiceData.items.map(
                      (item) => (
                        <div
                          key={item.name}
                          className="flex w-full justify-between gap-2 text-sm text-gray-500"
                        >
                          <div className="w-fit text-left">
                            <p>
                              {item.quantity}x {item.name}
                            </p>
                            {item.subscriptionPeriod && (
                              <p
                                className="text-xs"
                                data-identifier="invoice-item-subscription-period"
                              >
                                <Trans>Obdobje</Trans>:{" "}
                                {item.subscriptionPeriod.startFormatted} -{" "}
                                {item.subscriptionPeriod.endFormatted}
                              </p>
                            )}
                          </div>
                          <p className="w-full flex-1 text-right">
                            {item.totalWithTaxFormatted}
                          </p>
                        </div>
                      ),
                    )}

                    {invoice && invoice.giftCards.length > 0 ? (
                      <>
                        <Divider />

                        <p className="font-semibold">
                          <Trans>Izdani darilni boni</Trans>
                        </p>
                        {invoice.giftCards.map((giftCard) => (
                          <div
                            key={giftCard.code}
                            className="flex w-full justify-between gap-2 text-sm text-gray-500"
                          >
                            <Link
                              href={`/dashboard/gift-card/${giftCard.id}`}
                              showAnchorIcon
                              className="w-fit text-left"
                            >
                              {giftCard.code}
                            </Link>
                            <p className="w-full flex-1 text-right">
                              {giftCard.initialAmountFormatted}
                            </p>
                          </div>
                        ))}
                      </>
                    ) : undefined}
                  </Tab>
                  <Tab title={t`Aktivnost`} className="flex flex-col gap-4">
                    {invoice?.additionalReceiptData.activities.map(
                      (activity) => (
                        <div>
                          <InvoiceDetailRow
                            label={activity.label}
                            key={activity.label}
                            value={activity.date}
                          />
                          {activity.description && (
                            <p className="text-xs text-gray-500">
                              {activity.description}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </Tab>
                </Tabs>

                <Divider />
              </ModalBody>
              <ModalFooter className="flex flex-col">
                {isReadOnly ? (
                  <LimeAlert
                    color="warning"
                    className="mb-2"
                    data-identifier="archived-document-notice"
                    title={t`Dokument je v arhivirani organizaciji`}
                    description={t`Lahko ga pregledate in prenesete, spreminjanje pa ni več mogoče.`}
                  />
                ) : null}

                {invoice?.type === "estimate" && !isReadOnly ? (
                  <>
                    <Button
                      color="primary"
                      className="h-12"
                      data-identifier="convert-estimate-to-invoice"
                      startContent={<FileCheck2 size={16} />}
                      as={Link}
                      href={`/dashboard/tax-register/invoice/new?fromEstimate=${invoice.id}&redirectUrl=${encodeURIComponent(redirectUrl ?? window.location.href)}`}
                    >
                      <Trans>Pretvori v račun</Trans>
                    </Button>

                    <Divider className="my-2" />
                  </>
                ) : undefined}

                {invoice?.type === "estimate" || isReadOnly ? null : (
                  <>
                    <Button
                      isDisabled={
                        isPending ||
                        isError ||
                        isPostCancelInvoicePending ||
                        isPostSendInvoiceEmailPending ||
                        isPutOrganizationInvoicePrintCountPending
                      }
                      isLoading={isPrinting || isFetching}
                      className="h-12"
                      color="primary"
                      onPress={() => handlePrintReceipt({ type: "receipt" })}
                      data-identifier="print-receipt"
                      startContent={<PrinterCheck size={16} />}
                    >
                      <Trans>Ponovno tiskaj</Trans>
                    </Button>
                    {outstandingCents > 0 &&
                      !invoice?.paidInFull &&
                      !invoice?.canceled &&
                      !isCreditNote && (
                        <Button
                          isDisabled={
                            isPending ||
                            isError ||
                            isPostCancelInvoicePending ||
                            isPostSendInvoiceEmailPending
                          }
                          isLoading={
                            isPrinting ||
                            isFetching ||
                            isPutOrganizationInvoicePrintCountPending
                          }
                          className="h-12"
                          color="primary"
                          onPress={() => handlePrintReceipt({ type: "UPN" })}
                          data-identifier="print-bank-receipt"
                          startContent={<Printer size={16} />}
                        >
                          <Trans>Tiskaj UPN listek</Trans>
                        </Button>
                      )}
                    {invoice &&
                    canEditPayments &&
                    outstandingCents > 0 &&
                    !invoice.canceled &&
                    !isCreditNote ? (
                      <Button
                        isDisabled={
                          isPending ||
                          isError ||
                          isSubscriptionSharesLoading ||
                          isPostCancelInvoicePending ||
                          isPostSendInvoiceEmailPending
                        }
                        isLoading={isFetching}
                        className="h-12"
                        color="primary"
                        data-identifier="confirm-payment"
                        onPress={async () => {
                          if (sharesToMarkPaidOnPayment.length > 0) {
                            openSubscriptionSharePaymentModal();
                            return;
                          }

                          await settleOutstanding(false);
                        }}
                        startContent={<FileCheck2 size={16} />}
                      >
                        <Trans>Potrdi plačilo</Trans>
                      </Button>
                    ) : undefined}
                  </>
                )}

                {canChangeInvoicePaymentMethod(invoice, capabilities) ? (
                  <Button
                    isDisabled={
                      isPending ||
                      isError ||
                      isPostCancelInvoicePending ||
                      isPostSendInvoiceEmailPending
                    }
                    className="h-12"
                    color="primary"
                    onPress={() => {
                      openChangePaymentsModal();
                    }}
                    data-identifier="open-change-payments"
                    startContent={<Replace size={16} />}
                  >
                    <Trans>Spremeni način plačila</Trans>
                  </Button>
                ) : undefined}
                {emailRecipient.status !== "no-recipient" && !isReadOnly && (
                  <SendInvoiceEmailButton
                    recipient={emailRecipient}
                    isDisabled={
                      isPending ||
                      isError ||
                      isPrinting ||
                      isPostCancelInvoicePending
                    }
                    isLoading={isPostSendInvoiceEmailPending}
                    onSend={async (recipientEmail) => {
                      if (!invoiceId) return;

                      await postSendInvoiceEmail({
                        invoiceId,
                        body: { recipientEmail },
                      });
                    }}
                  />
                )}

                <Button
                  isDisabled={
                    isPending ||
                    isError ||
                    isPrinting ||
                    isGetDownloadInvoicePending
                  }
                  isLoading={isGetDownloadInvoicePending}
                  className="h-12"
                  color="primary"
                  startContent={<Download size={16} />}
                  onPress={async () => {
                    if (!invoiceId) return;

                    const response = await getDownloadInvoice({
                      invoiceId,
                    });

                    if (!response) return;

                    const fileName = `${invoice?.type === "credit-note" ? "credit-note" : "invoice"}_${invoice?.number}.pdf`;

                    try {
                      await saveBase64File({
                        base64: response,
                        fileName,
                        mimeType: "application/pdf",
                      });
                    } catch (error) {
                      addToast({
                        title:
                          error instanceof Error
                            ? error.message
                            : t`Prenos dokumenta ni uspel`,
                        color: "danger",
                      });
                    }
                  }}
                >
                  <Trans>Prenesi dokument</Trans>
                </Button>

                {invoice?.canceled || isCreditNote || isReadOnly ? undefined : (
                  <>
                    <Divider className="my-2" />

                    <form
                      onSubmit={form.onSubmit(async () => {
                        if (sharesToMarkUnpaidOnCancel.length > 0) {
                          openSubscriptionShareCancelModal();
                          return;
                        }

                        await cancelInvoice(false);
                      })}
                      className="flex flex-col gap-2"
                    >
                      {invoice?.type === "estimate" ? null : (
                        <LimeTextarea
                          placeholder={t`Razlog stornacije`}
                          variant="bordered"
                          {...form.getInputProps("cancellationReason")}
                          data-identifier="cancellation-reason"
                        />
                      )}
                      <Button
                        isDisabled={
                          isPending ||
                          isError ||
                          isSubscriptionSharesLoading ||
                          isPrinting ||
                          isPostSendInvoiceEmailPending
                        }
                        isLoading={isPostCancelInvoicePending}
                        className="h-12"
                        color="primary"
                        type="submit"
                        data-identifier="cancel-invoice"
                        startContent={<FileX2 size={16} />}
                      >
                        <Trans>Storniraj</Trans>
                      </Button>
                    </form>
                  </>
                )}

                <LimeAlert
                  color="danger"
                  message={postSendInvoiceEmailErrorMessage}
                />
                <LimeAlert
                  color="danger"
                  message={postCancelInvoiceErrorMessage}
                />
                <LimeAlert color="danger" message={getInvoiceErrorMessage} />
                <LimeAlert
                  color="danger"
                  message={getSubscriptionSharesErrorMessage}
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <SubscriptionShareModal
        variant="markUnpaid"
        isOpen={isSubscriptionShareCancelModalOpen}
        shares={sharesToMarkUnpaidOnCancel}
        isLoading={isPostCancelInvoicePending}
        onConfirm={(markSubscriptionSharesUnpaid) =>
          cancelInvoice(markSubscriptionSharesUnpaid).finally(
            closeSubscriptionShareCancelModal,
          )
        }
        onClose={closeSubscriptionShareCancelModal}
      />

      <SubscriptionShareModal
        variant="markPaid"
        isOpen={isSubscriptionSharePaymentModalOpen}
        shares={sharesToMarkPaidOnPayment}
        isLoading={isPutInvoicePaymentMethodPending}
        onConfirm={(markSubscriptionSharesPaid) =>
          settleOutstanding(markSubscriptionSharesPaid).finally(
            closeSubscriptionSharePaymentModal,
          )
        }
        onClose={closeSubscriptionSharePaymentModal}
      />

      {invoice != null && (
        <SplitPaymentModal
          isTrrReady={isTrrReady}
          existingData={invoice.additionalReceiptData.payments.map(
            (payment) => {
              if (payment.type === "coupon") {
                if (payment.isThirdParty === true) {
                  return {
                    type: "coupon" as const,
                    isThirdParty: true as const,
                    amountCents: payment.amount * 100,
                    giftCardCode: payment.code?.split("-")[1] ?? "N/A",
                    giftCardPrefix: payment.code?.split("-")[0] ?? "N/A",
                  };
                } else {
                  return {
                    type: "coupon" as const,
                    isThirdParty: false as const,
                    amountCents: payment.amount * 100,
                    giftCardCode: payment.code ?? "N/A",
                    isLinkedToAppointment: false,
                  };
                }
              }

              return {
                type: payment.type as Exclude<
                  SplitPaymentForm["options"][number]["type"],
                  "coupon"
                >,
                amountCents: payment.amount * 100,
              };
            },
          )}
          handleClose={async (data) => {
            if (!data) {
              closeChangePaymentsModal();
              return;
            }

            const payments = data.options.flatMap((option) => {
              // Filter entries where the value is valid and greater than 0
              if (!(option.amountCents && Number(option.amountCents) > 0)) {
                return [];
              }

              const amountCents = Number(option.amountCents);

              const entry =
                option.type === "coupon"
                  ? option.isThirdParty
                    ? {
                        type: option.type,
                        isThirdParty: true as const,
                        amountCents,
                        giftCardCode: option.giftCardCode,
                        giftCardPrefix: option.giftCardPrefix,
                      }
                    : {
                        type: option.type,
                        isThirdParty: false as const,
                        amountCents,
                        giftCardCode: option.giftCardCode,
                      }
                  : {
                      type: option.type,
                      amountCents,
                    };

              return [entry];
            });

            await putInvoicePaymentMethod({
              invoiceId: invoice.id,
              body: {
                payments,
              },
            });

            refetch();
            closeChangePaymentsModal();
          }}
          isOpen={isChangePaymentsModalOpen}
          isLoading={isPutInvoicePaymentMethodPending}
          errorMessage={putInvoicePaymentMethodErrorMessage}
          totalPriceCents={invoice.totalWithTax * 100}
          locale="sl"
          currency="EUR"
          isChangingPaymentOnExistingInvoice
        />
      )}
    </>
  );
};

const InvoiceDetailRow = ({
  label,
  value,
  isSkeleton,
}: {
  label: string;
  value?: string | React.ReactNode;
  isSkeleton?: boolean;
}) => {
  return (
    <div className="flex w-full justify-between">
      <Skeleton isLoaded={!isSkeleton}>
        <p className="text-xs font-semibold">{label}</p>
      </Skeleton>
      <Skeleton isLoaded={!isSkeleton}>
        {typeof value === "string" ? (
          <p className="text-sm text-gray-500">{value}</p>
        ) : (
          value
        )}
      </Skeleton>
    </div>
  );
};
